// CandidateEvidencePanel — the candidate record, full screen over the talent
// pool, on the EquaOS design system: who they are and where they stand, then
// Tabs for the evidence, the communication history and other role matches.
// Room to grow (inbound replies, interview notes, offer status) as those are
// captured. The only call is GET /resumes/:id/cv-url, as before.
import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { Card, CardHeader, StatusPill, Tabs, Button, EmptyState, Icon, useShellLayout } from '../../ds';
import { stageTone, matchColor } from './stage';
import { CV_UNREAD_LABEL } from '../../utils/cvRead';

function ScoreBar({ label, value }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 'var(--fs-body-sm)', marginBottom: 4 }}>
        <span style={{ color: 'var(--text-2)', fontWeight: 500 }}>{label}</span>
        <span className="num" style={{ fontWeight: 600, color: 'var(--text-1)' }}>{value}%</span>
      </div>
      <div style={{ height: 8, background: 'var(--chart-track)', borderRadius: 'var(--radius-pill)', overflow: 'hidden' }}
        role="meter" aria-valuemin={0} aria-valuemax={100} aria-valuenow={value} aria-label={label}>
        <div style={{ height: '100%', width: `${value}%`, background: 'var(--chart-1)', borderRadius: 'var(--radius-pill)' }} />
      </div>
    </div>
  );
}

function Fact({ label, children }) {
  return (
    <span><span style={{ color: 'var(--text-muted)' }}>{label}</span> {children}</span>
  );
}

function Bullets({ items, tone }) {
  return (
    <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gap: 6 }}>
      {items.map((s, i) => (
        <li key={i} style={{ display: 'flex', gap: 8, fontSize: 'var(--fs-body-md)', color: 'var(--text-2)' }}>
          <span aria-hidden="true" style={{ width: 6, height: 6, borderRadius: '50%', background: `var(--status-${tone}-text)`, marginTop: 9, flex: 'none' }} />
          <span>{s}</span>
        </li>
      ))}
    </ul>
  );
}

/* Add this candidate to another role, as a new application (they keep the
   first). The walkthrough, 30 Oct 2026: the AI suggests other roles, "you
   guys have to click the button to actually add them", and the candidate is
   told "you have also been considered for this role". Suggested roles come
   first; any other vacancy can be picked. */
function AddToRole({ candidate, recs }) {
  const [jobs, setJobs] = useState([]);
  const [jobId, setJobId] = useState('');
  const [tell, setTell] = useState(true);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null); // { tone, text }

  useEffect(() => {
    api.get('/jobs', { params: { limit: 100 } })
      .then((r) => setJobs(r.data?.data?.jobs || r.data?.data || r.data?.jobs || []))
      .catch(() => setJobs([]));
  }, []);

  const current = String(candidate.appliedJDId || candidate.jobId?._id || candidate.jobId || '');
  const suggested = recs.filter((r) => r.jobId && String(r.jobId) !== current);
  const suggestedIds = new Set(suggested.map((r) => String(r.jobId)));
  const others = (Array.isArray(jobs) ? jobs : []).filter((j) => String(j._id) !== current && !suggestedIds.has(String(j._id)));

  const add = async () => {
    if (!jobId) return;
    setBusy(true); setMsg(null);
    try {
      const r = await api.post(`/talent-pool/jobs/${jobId}/existing-cvs/add`, { resumeIds: [candidate._id], tellCandidates: tell });
      const d = r.data?.data || {};
      if (d.added?.length) {
        const told = d.told?.told?.length ? ' and emailed that they are being considered' : (tell && d.told?.notTold?.length ? ` — not emailed: ${d.told.notTold[0].why}` : '');
        setMsg({ tone: 'success', text: `Added to ${d.jobTitle} as a new application${told}. Their first application carries on.` });
      } else if (d.alreadyOnJob?.length) {
        setMsg({ tone: 'info', text: `Already on ${d.jobTitle}.` });
      } else {
        setMsg({ tone: 'warning', text: 'Nobody was added.' });
      }
    } catch (e) {
      setMsg({ tone: 'danger', text: e.response?.data?.message || 'Could not add them. Please try again.' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card>
      <CardHeader title="Add to another role" subtitle="A new application for that role; this one carries on as it is." />
      <div style={{ display: 'grid', gap: 10 }}>
        <select value={jobId} onChange={(e) => setJobId(e.target.value)} aria-label="Role"
          style={{ padding: '8px 10px', borderRadius: 'var(--radius-md)', border: '1px solid var(--border)', background: 'var(--surface-1)', color: 'var(--text-1)', font: 'inherit' }}>
          <option value="">Choose a role…</option>
          {suggested.length > 0 && (
            <optgroup label="Suggested for this candidate">
              {suggested.map((r) => <option key={String(r.jobId)} value={String(r.jobId)}>{r.jobTitle} — {r.matchScore}% match</option>)}
            </optgroup>
          )}
          {others.length > 0 && (
            <optgroup label="Other roles">
              {others.map((j) => <option key={j._id} value={j._id}>{j.title}</option>)}
            </optgroup>
          )}
        </select>
        <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 'var(--fs-body-sm)', color: 'var(--text-2)' }}>
          <input type="checkbox" checked={tell} onChange={(e) => setTell(e.target.checked)} />
          Email them that they are also being considered for this role
        </label>
        <div><Button onClick={add} disabled={!jobId || busy}>{busy ? 'Adding…' : 'Add to this role'}</Button></div>
        {msg && <StatusPill tone={msg.tone} dot={false}>{msg.text}</StatusPill>}
      </div>
    </Card>
  );
}

// @story HIR-HR3
export default function CandidateEvidencePanel({ candidate, onClose }) {
  const [cvBusy, setCvBusy] = useState('');
  const [tab, setTab] = useState('evidence');
  const { mobile } = useShellLayout();

  // Esc closes the full-screen panel.
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  if (!candidate) return null;

  const openCv = (download) => {
    setCvBusy(download ? 'download' : 'view');
    api.get(`/resumes/${candidate._id}/cv-url${download ? '?download=1' : ''}`)
      .then((r) => {
        if (r.data?.available && r.data.url) window.open(r.data.url, '_blank', 'noopener');
        else alert('No CV file is on record for this candidate.');
      })
      .catch(() => alert('Could not open the CV — please try again.'))
      .finally(() => setCvBusy(''));
  };

  const scores = candidate.scores || {};
  const hasScores = scores.preScreening > 0 || scores.psychoanalytical > 0 || scores.finalInterview > 0;
  const messages = Array.isArray(candidate.messageLog) ? candidate.messageLog : [];
  const recs = candidate.recommendations || [];
  const strengths = candidate.strengths || [];
  const concerns = candidate.concerns || [];

  const tabs = [
    { value: 'evidence', label: 'Evidence' },
    { value: 'messages', label: `Messages${messages.length ? ` (${messages.length})` : ''}` },
    { value: 'roles', label: `Other roles${recs.length ? ` (${recs.length})` : ''}` },
  ];

  return (
    <div className="eq-ds eq-page" role="dialog" aria-modal="true" aria-labelledby="candidate-record-title"
      style={{ position: 'fixed', inset: 0, zIndex: 50, overflowY: 'auto' }}>
      <div style={{ maxWidth: 1040, margin: '0 auto', padding: mobile ? '16px 16px 104px' : '24px 32px 104px', display: 'grid', gap: 20 }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
          <div style={{ minWidth: 0 }}>
            <Button variant="ghost" size="sm" icon={<Icon name="arrow-left" />} onClick={onClose}>
              Back to talent pool
            </Button>
            <h1 id="candidate-record-title" style={{ fontSize: 'var(--fs-display-md)', marginTop: 8 }}>{candidate.candidateName}</h1>
            <p style={{ color: 'var(--text-2)', marginTop: 2 }}>{candidate.email}</p>
            {/* The facts a recruiter reaches for first — contact and which
                application this is. */}
            <div style={{ marginTop: 6, display: 'flex', flexWrap: 'wrap', gap: '4px 16px', fontSize: 'var(--fs-body-sm)', color: 'var(--text-1)' }}>
              {candidate.phone && <Fact label="Phone">{candidate.phone}</Fact>}
              {candidate.applicationId && <Fact label="Application">{candidate.applicationId}</Fact>}
              {candidate.jdNumber && <Fact label="JD">{candidate.jdNumber}</Fact>}
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 'none' }}>
            <Button variant="secondary" onClick={() => openCv(false)} disabled={!!cvBusy} loading={cvBusy === 'view'} icon={<Icon name="file-text" />}>
              View CV
            </Button>
            <Button variant="secondary" onClick={() => openCv(true)} disabled={!!cvBusy} loading={cvBusy === 'download'} icon={<Icon name="download" />}
              aria-label="Download CV" title="Download CV" />
            <Button variant="ghost" onClick={onClose} icon={<Icon name="x" />} aria-label="Close" />
          </div>
        </div>

        {/* Where they stand */}
        <Card>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
            <StatusPill tone={stageTone(candidate.workflowStage)}>{candidate.workflowStage}</StatusPill>
            {candidate.cvUnread || candidate.matchScore === null ? (
              /* No score is made without the CV (6 Oct 2026). */
              <StatusPill tone="warning">
                {CV_UNREAD_LABEL}{candidate.cvUnreadReason ? ` — ${candidate.cvUnreadReason}` : ''}
              </StatusPill>
            ) : (
              <>
                <span className="num" style={{ fontSize: 'var(--fs-display-md)', fontWeight: 700, color: matchColor(candidate.matchScore) }}>{candidate.matchScore}%</span>
                <span style={{ color: 'var(--text-muted)', fontSize: 'var(--fs-body-sm)' }}>match for {candidate.appliedJD}</span>
              </>
            )}
          </div>
        </Card>

        <div className="eq-scroll-x">
          <div>
            <Tabs items={tabs} value={tab} onChange={setTab} />
          </div>
        </div>

        {tab === 'evidence' && (
          <div style={{ display: 'grid', gap: 20 }}>
            {hasScores && (
              <Card>
                <CardHeader title="Assessment performance" />
                {scores.ats > 0 && <ScoreBar label="ATS score" value={scores.ats} />}
                {scores.preScreening > 0 && <ScoreBar label="Pre-screening" value={scores.preScreening} />}
                {scores.psychoanalytical > 0 && <ScoreBar label="Psychoanalytical" value={scores.psychoanalytical} />}
                {scores.finalInterview > 0 && <ScoreBar label="Final interview" value={scores.finalInterview} />}
              </Card>
            )}

            <Card>
              <CardHeader title="Match evidence" subtitle={candidate.appliedJD} />
              {strengths.length === 0 && concerns.length === 0 && !(candidate.summary && typeof candidate.summary === 'string') && (
                <p style={{ color: 'var(--text-muted)' }}>No match evidence recorded yet.</p>
              )}
              {strengths.length > 0 && (
                <div style={{ marginBottom: 16 }}>
                  <h4 style={{ fontSize: 'var(--fs-body-sm)', color: 'var(--status-success-text)', display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                    <Icon name="check-circle" /> Strengths
                  </h4>
                  <Bullets items={strengths} tone="success" />
                </div>
              )}
              {concerns.length > 0 && (
                <div style={{ marginBottom: 16 }}>
                  <h4 style={{ fontSize: 'var(--fs-body-sm)', color: 'var(--status-danger-text)', display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                    <Icon name="warning-circle" /> Concerns
                  </h4>
                  <Bullets items={concerns} tone="danger" />
                </div>
              )}
              {candidate.summary && typeof candidate.summary === 'string' && (
                <p style={{ fontSize: 'var(--fs-body-md)', color: 'var(--text-2)', background: 'var(--surface-2)', borderRadius: 'var(--radius-md)', padding: 12 }}>{candidate.summary}</p>
              )}
            </Card>

            {candidate.finalInterviewFeedback && (
              <Card>
                <CardHeader title="Interview insight" />
                <p style={{ color: 'var(--text-2)' }}>{candidate.finalInterviewFeedback}</p>
              </Card>
            )}
          </div>
        )}

        {/* Communication history — every email/WhatsApp to the candidate,
            newest first, with dates. */}
        {tab === 'messages' && (
          <Card padding={messages.length ? '8px 20px' : '20px'}>
            {messages.length === 0 ? (
              <EmptyState icon={<Icon name="chat-circle-text" size={36} />} title="No messages yet" description="Emails and WhatsApp messages sent to this candidate appear here." />
            ) : (
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {messages.map((m, i) => (
                  <li key={i} style={{ padding: '12px 0', borderBottom: i < messages.length - 1 ? '1px solid var(--border)' : 'none' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                      <span style={{ fontWeight: 500, color: 'var(--text-1)' }}>{m.label || String(m.gate || '').replace(/_/g, ' ')}</span>
                      <span className="num" style={{ fontSize: 'var(--fs-caption)', color: 'var(--text-muted)' }}>{m.at ? new Date(m.at).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' }) : ''}</span>
                    </div>
                    <div style={{ fontSize: 'var(--fs-caption)', color: 'var(--text-muted)', marginTop: 2 }}>
                      {(m.channels || []).map((c) => (c === 'whatsapp' ? `WhatsApp${m.whatsappNotDelivered ? ' (not delivered)' : ''}` : 'Email')).join(' · ') || '—'}
                      {m.sentBy ? ` · sent by ${m.sentBy}` : ''}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        )}

        {/* Alternative JD recommendations */}
        {tab === 'roles' && <div style={{ marginBottom: 16 }}><AddToRole candidate={candidate} recs={recs} /></div>}
        {tab === 'roles' && (
          recs.length === 0 ? (
            <Card>
              <EmptyState icon={<Icon name="medal" size={36} />} title="No role matches yet"
                description="Choose Generate in the talent pool table to find other roles this candidate fits." />
            </Card>
          ) : (
            <div style={{ display: 'grid', gap: 16 }}>
              {recs.map((rec, i) => (
                <Card key={i}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 8 }}>
                    <span style={{ fontWeight: 600, color: 'var(--text-1)' }}>{rec.jobTitle}</span>
                    <span className="num" style={{ fontWeight: 700, fontSize: 'var(--fs-body-lg)', color: 'var(--text-1)' }}>{rec.matchScore}%</span>
                  </div>
                  <div style={{ height: 6, background: 'var(--chart-track)', borderRadius: 'var(--radius-pill)', overflow: 'hidden', marginBottom: 12 }}>
                    <div style={{ height: '100%', width: `${rec.matchScore}%`, background: 'var(--chart-1)' }} />
                  </div>
                  {(rec.evidence || []).length > 0 && <Bullets items={rec.evidence} tone="info" />}
                </Card>
              ))}
            </div>
          )
        )}
      </div>
    </div>
  );
}
